import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import Header from "components/Header";
import TextBox from "components/TextBox";
import { AppRoutes } from "pages/constants/routes";
import AuthService from "api/Auth/AuthService";
import UserService from "api/User/UserService";
import { setUser } from "store/userSlice";
import { signInSchema } from "validation/schemas/signin";

import styles from "./SignIn.module.scss";

interface ValidationErrors {
  email?: string;
  password?: string;
  auth?: string;
}

const SignIn = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const validateForm = (): boolean => {
    try {
      const result = signInSchema.safeParse({ email: email.trim(), password });

      if (!result.success) {
        const newErrors: ValidationErrors = {};

        result.error.errors.forEach((error) => {
          const field = error.path[0] as keyof ValidationErrors;
          if (!newErrors[field]) {
            newErrors[field] = error.message;
          }
        });
        setErrors(newErrors);
        return false;
      }
      setErrors({});
      return true;
    } catch (error) {
      setErrors({ auth: "Validation failed. Please try again." });
      return false;
    }
  };

  const onFormChange =
    (onChange: (newValue: string) => void) => (newValue: string) => {
      setErrors({});
      onChange(newValue);
    };

  const submitPassword = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await AuthService.login(email, password);
      if (response.success) {
        const userResponse = await UserService.getSelf();
        dispatch(
          setUser({
            loggedIn: true,
            username: userResponse.username,
            email: userResponse.email,
            hasPrivilegedAccess: userResponse.has_privileged_access,
          }),
        );
        navigate(AppRoutes.home);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      // Handle different types of errors
      if (err.status === 401) {
        setErrors((prev) => ({
          ...prev,
          auth: "Invalid email or password",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          auth: "An error occurred. Please try again",
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.background} />
      <Header hideSignIn />
      <div className={styles.content}>
        <div className={styles.container}>
          <span className={styles.title}>Welcome Back!</span>
          {errors.auth && (
            <div className={styles.errorMessage}>
              <span>{errors.auth}</span>
            </div>
          )}
          <div className={styles.signInContainer}>
            <div className={styles.signUpContainer}>
              <Link to={AppRoutes.signup}>Sign Up Here {"-->"}</Link>
            </div>
            <div className={styles.inputWrapper}>
              <TextBox
                autoFocus
                placeholder="Email"
                value={email}
                onChange={onFormChange(setEmail)}
                error={!!errors.email}
              />
              {errors.email && (
                <span className={styles.fieldError}>{errors.email}</span>
              )}
            </div>

            <div className={styles.inputWrapper}>
              <TextBox
                password
                placeholder="Password"
                value={password}
                onChange={onFormChange(setPassword)}
                onSubmit={!isSubmitting ? submitPassword : undefined}
                error={!!errors.password}
              />
              {errors.password && (
                <span className={styles.fieldError}>{errors.password}</span>
              )}
            </div>

            <div className={styles.fullWidthContainer}>
              <Link
                to={AppRoutes.resetPasswordRequest}
                className={styles.forgotPassword}
              >
                Forgot your password?
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
